/**
 * Utils.js - Shared utility functions
 *
 * This module provides:
 * - Distance calculation using OpenLayers geodesic functions
 * - Unit conversion constants and functions (Single Source of Truth)
 * - UI helper functions (compassIcon)
 *
 * @module Utils
 */

import { getLength, getDistance } from 'ol/sphere';
import LineString from 'ol/geom/LineString';

// ============================================================================
// Distance Constants (Single Source of Truth)
// ============================================================================

/**
 * WGS84 mean Earth radius in meters (same as OpenLayers default)
 * @type {number}
 */
export const EARTH_RADIUS_METERS = 6371008.8;

/**
 * Unit conversion factors from metric
 * @type {Object}
 */
export const CONVERSION_FACTORS = {
  KM_TO_MILES: 0.621371,
  KM_TO_SEAMILES: 0.539957,
  M_TO_YARDS: 1.09361,
  M_TO_FEET: 3.28084,
  M_TO_INCHES: 39.3701
};

// ============================================================================
// Unit Conversion Functions
// ============================================================================

/**
 * Convert meters to all supported distance units.
 * This is the Single Source of Truth for unit conversion in the app.
 *
 * @param {number} meters - Distance in meters
 * @param {number} [precision=3] - Decimal precision for output
 * @returns {Object} Distance object with all units as strings
 */
export function formatDistanceUnits (meters, precision = 3) {
  const m = meters || 0;
  const km = m / 1000;

  return {
    m: m.toFixed(precision),
    km: km.toFixed(precision),
    miles: (km * CONVERSION_FACTORS.KM_TO_MILES).toFixed(precision),
    seamiles: (km * CONVERSION_FACTORS.KM_TO_SEAMILES).toFixed(precision),
    yards: (m * CONVERSION_FACTORS.M_TO_YARDS).toFixed(precision),
    feet: (m * CONVERSION_FACTORS.M_TO_FEET).toFixed(precision),
    inches: (m * CONVERSION_FACTORS.M_TO_INCHES).toFixed(precision)
  };
}

// ============================================================================
// Distance Calculation Functions
// ============================================================================

/**
 * Flatten coordinates if they are nested (MultiLineString format).
 * Handles LineString, MultiLineString, and deeper nesting.
 *
 * @param {Array} coordinates - Coordinate array (may be nested)
 * @returns {Array} Flat array of coordinate points
 */
export function flattenCoordinates (coordinates) {
  if (!coordinates || coordinates.length === 0) {
    return [];
  }

  // Check if it's MultiLineString format (array of arrays of points)
  // e.g., [[[lon, lat], [lon, lat]], [[lon, lat], [lon, lat]]]
  if (Array.isArray(coordinates[0]?.[0]) && typeof coordinates[0][0][0] === 'number') {
    return coordinates.flat();
  }

  // Check if it's deeply nested (array of MultiLineStrings)
  if (Array.isArray(coordinates[0]?.[0]?.[0])) {
    return coordinates.flat(2);
  }

  // Already flat LineString format: [[lon, lat], [lon, lat], ...]
  return coordinates;
}

/**
 * Calculate total distance from GeoJSON coordinates using OpenLayers geodesic calculation.
 * Uses getLength() which computes the sum of great circle distances.
 *
 * This is the preferred method for calculating track distances as it uses
 * OpenLayers' accurate WGS84-based geodesic calculation.
 *
 * @param {Array} coordinates - GeoJSON coordinate array [lon, lat, ...] or nested
 * @returns {Object} Distance in all units (m, km, miles, seamiles, yards, feet, inches)
 */
export function calculateDistanceFromCoordinates (coordinates) {
  const flatCoords = flattenCoordinates(coordinates);

  if (!flatCoords || flatCoords.length < 2) {
    return formatDistanceUnits(0);
  }

  // Create a LineString geometry for OpenLayers
  // Coordinates must be in EPSG:4326 (lon, lat) format
  const line = new LineString(flatCoords);

  // getLength calculates geodesic (great circle) distance
  // projection: 'EPSG:4326' tells OL the input coords are geographic
  const meters = getLength(line, { projection: 'EPSG:4326' });

  return formatDistanceUnits(meters);
}

/**
 * Calculate distance between two geographic points using OpenLayers.
 * Useful for incremental distance calculation during GPS recording.
 *
 * @param {Array} coord1 - First coordinate [lon, lat]
 * @param {Array} coord2 - Second coordinate [lon, lat]
 * @returns {number} Distance in meters
 */
export function calculateDistanceBetweenPoints (coord1, coord2) {
  if (!coord1 || !coord2 || coord1.length < 2 || coord2.length < 2) {
    return 0;
  }
  // getDistance expects [lon, lat] format (EPSG:4326)
  return getDistance(coord1, coord2);
}

// ============================================================================
// UI Helper Functions
// ============================================================================

/**
 * Create new icon for the rotate button to be re-assigned
 *
 * @src https://florianbrinkmann.com/en/svg-use-element-javascript-4513/
 * @returns {SVGSVGElement} SVG element for compass icon
 */
export function compassIcon () {
  const svgElem = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  const useElem = document.createElementNS('http://www.w3.org/2000/svg', 'use');

  svgElem.setAttribute('role', 'button');
  svgElem.setAttribute('aria-label', 'compass');
  svgElem.classList.add('ol-compass', 'compass-svg', 'block', 'w-5', 'h-5', 'cursor-pointer', 'select-none', 'z-120');

  useElem.setAttributeNS('http://www.w3.org/1999/xlink', 'xlink:href', '#compass');
  svgElem.appendChild(useElem);

  return svgElem;
}

// ============================================================================
// Default Export (for backwards compatibility)
// ============================================================================

const Utils = {
  compassIcon,
  // Distance functions available via named exports
  formatDistanceUnits,
  calculateDistanceFromCoordinates,
  calculateDistanceBetweenPoints,
  flattenCoordinates,
  CONVERSION_FACTORS,
  EARTH_RADIUS_METERS
};

export default Utils;
